import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useInactivityLogout } from '../hooks/useInactivityLogout';
import { Header } from '../components/Header';
import { StatusBadge, ViolationTypeBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { adminService } from '../services/api';
import ViolationsMap from '../components/ViolationsMap';
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line
} from 'recharts';
import {
    FileText,
    Clock,
    CheckCircle2,
    XCircle,
    Loader2,
    Users,
    Shield,
    Activity,
    TrendingUp,
    Search,
    UserCog,
    Eye,
    Server,
    Ban,
    UserCheck,
    RefreshCw,
    AlertTriangle,
    UserPlus,
    BadgeCheck,
    Hash,
    MapPin
} from 'lucide-react';
import { format } from 'date-fns';

const COLORS = {
    pending: '#F59E0B',
    approved: '#10B981',
    rejected: '#EF4444'
};

export default function AdminDashboard() {
    const { user, token } = useAuth();
    useInactivityLogout(); // auto-logout after 5 min idle (sensitive admin surface)
    const [stats, setStats] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [users, setUsers] = useState([]);
    const [violations, setViolations] = useState([]);
    const [apiLogs, setApiLogs] = useState([]);
    const [retryQueue, setRetryQueue] = useState([]);
    const [retryingId, setRetryingId] = useState(null);
    const [officerApplications, setOfficerApplications] = useState([]);
    const [appActionLoading, setAppActionLoading] = useState(null);
    const [auditLogs, setAuditLogs] = useState([]);
    const [officerPerformance, setOfficerPerformance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    
    // Filters
    const [userSearch, setUserSearch] = useState('');
    const [userRoleFilter, setUserRoleFilter] = useState('all');
    const [violationStatusFilter, setViolationStatusFilter] = useState('all');

    const fetchStats = useCallback(async () => {
        try {
            const response = await adminService.stats();
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats');
        }
    }, [token]);

    const fetchAnalytics = useCallback(async () => {
        try {
            const response = await adminService.analytics();
            setAnalytics(response.data);
        } catch (error) {
            console.error('Failed to fetch analytics');
        }
    }, [token]);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await adminService.users(userRoleFilter !== 'all' ? userRoleFilter : undefined);
            setUsers(response.data);
        } catch (error) {
            toast.error('Failed to fetch users');
        }
    }, [userRoleFilter]);

    const fetchViolations = useCallback(async () => {
        try {
            const response = await adminService.violations(
                violationStatusFilter !== 'all' ? { status: violationStatusFilter } : {}
            );
            setViolations(response.data.violations);
        } catch (error) {
            toast.error('Failed to fetch violations');
        }
    }, [violationStatusFilter]);

    const fetchApiLogs = useCallback(async () => {
        try {
            const response = await adminService.apiLogs();
            setApiLogs(response.data);
        } catch (error) {
            console.error('Failed to fetch API logs');
        }
    }, [token]);

    const fetchRetryQueue = useCallback(async () => {
        try {
            const response = await adminService.apiQueue();
            setRetryQueue(response.data.queue || []);
        } catch (error) {
            console.error('Failed to fetch retry queue');
        }
    }, [token]);

    const fetchOfficerApplications = useCallback(async () => {
        try {
            const response = await adminService.officerApplications();
            setOfficerApplications(response.data || []);
        } catch (error) {
            console.error('Failed to fetch officer applications');
        }
    }, [token]);

    const fetchAuditLogs = useCallback(async () => {
        try {
            const response = await adminService.auditLogs();
            setAuditLogs(response.data || []);
        } catch (error) {
            console.error('Failed to fetch audit logs');
        }
    }, []);

    const fetchOfficerPerformance = useCallback(async () => {
        try {
            const response = await adminService.officerPerformance();
            setOfficerPerformance(response.data || []);
        } catch (error) {
            console.error('Failed to fetch officer performance');
        }
    }, []);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            await Promise.all([
                fetchStats(),
                fetchAnalytics(),
                fetchUsers(),
                fetchViolations(),
                fetchApiLogs(),
                fetchRetryQueue(),
                fetchOfficerApplications(),
                fetchAuditLogs(),
                fetchOfficerPerformance(),
            ]);
            setLoading(false);
        };
        fetchAll();
    }, [fetchStats, fetchAnalytics, fetchUsers, fetchViolations, fetchApiLogs, fetchRetryQueue, fetchOfficerApplications, fetchAuditLogs, fetchOfficerPerformance]);

    const handleRoleChange = async (userId, newRole) => {
        try {
            await adminService.updateRole(userId, newRole);
            toast.success('User role updated');
            fetchUsers();
        } catch (error) {
            toast.error('Failed to update role');
        }
    };

    const handleToggleSuspend = async (userId, currentlySuspended) => {
        try {
            const next = !currentlySuspended;
            await adminService.suspendUser(userId, next);
            toast.success(next ? 'User suspended' : 'User reactivated');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update suspension');
        }
    };

    const handleRetryGovtApi = async (violationId) => {
        setRetryingId(violationId);
        try {
            const resp = await adminService.retryApi(violationId);
            if (resp.data?.api_response?.success) {
                toast.success(`Sent successfully (ref: ${resp.data.api_response.reference_id})`);
            } else {
                toast.warning(`Retry attempted but still failing — will remain in queue`);
            }
            await Promise.all([fetchRetryQueue(), fetchApiLogs()]);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Retry failed');
        } finally {
            setRetryingId(null);
        }
    };

    const handleApproveOfficer = async (userId) => {
        setAppActionLoading(userId);
        try {
            await adminService.approveOfficer(userId);
            toast.success('Officer application approved');
            await Promise.all([fetchOfficerApplications(), fetchStats()]);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to approve application');
        } finally {
            setAppActionLoading(null);
        }
    };

    const handleRejectOfficer = async (userId) => {
        setAppActionLoading(userId);
        try {
            await adminService.rejectOfficer(userId);
            toast.success('Officer application rejected');
            await fetchOfficerApplications();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reject application');
        } finally {
            setAppActionLoading(null);
        }
    };

    const pendingApplications = officerApplications.filter(a => a.role === 'officer_pending');
    const rejectedApplications = officerApplications.filter(a => a.role === 'officer_rejected');

    const filteredUsers = users.filter(u =>
        u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.email?.toLowerCase().includes(userSearch.toLowerCase())
    );

    const pieData = analytics?.by_status?.map(s => ({
        name: s.status?.charAt(0).toUpperCase() + s.status?.slice(1),
        value: s.count,
        fill: COLORS[s.status] || '#888'
    })) || [];

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <Header />
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            <Header />
            
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
                        <p className="text-muted-foreground mt-1">
                            System overview and management
                        </p>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 rounded-lg">
                        <Shield className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium">Admin: {user?.name}</span>
                    </div>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="overview" data-testid="tab-overview">
                            <Activity className="w-4 h-4 mr-2" />
                            Overview
                        </TabsTrigger>
                        <TabsTrigger value="users" data-testid="tab-users">
                            <Users className="w-4 h-4 mr-2" />
                            Users
                        </TabsTrigger>
                        <TabsTrigger value="officer-applications" data-testid="tab-officer-applications">
                            <UserPlus className="w-4 h-4 mr-2" />
                            Officer Applications
                            {pendingApplications.length > 0 && (
                                <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full bg-warning text-warning-foreground">
                                    {pendingApplications.length}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="violations" data-testid="tab-violations">
                            <FileText className="w-4 h-4 mr-2" />
                            Violations
                        </TabsTrigger>
                        <TabsTrigger value="logs" data-testid="tab-logs">
                            <Server className="w-4 h-4 mr-2" />
                            API Logs
                        </TabsTrigger>
                        <TabsTrigger value="performance" data-testid="tab-performance">
                            <TrendingUp className="w-4 h-4 mr-2" />
                            Performance
                        </TabsTrigger>
                        <TabsTrigger value="map" data-testid="tab-map">
                            <MapPin className="w-4 h-4 mr-2" />
                            Map
                        </TabsTrigger>
                        <TabsTrigger value="audit" data-testid="tab-audit">
                            <Shield className="w-4 h-4 mr-2" />
                            Audit
                        </TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="space-y-6">
                        {/* Stats Cards */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <FileText className="w-6 h-6 text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-3xl font-bold">{stats?.total_reports || 0}</p>
                                            <p className="text-xs text-muted-foreground">Total Reports</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
                                            <Clock className="w-6 h-6 text-warning" />
                                        </div>
                                        <div>
                                            <p className="text-3xl font-bold">{stats?.pending || 0}</p>
                                            <p className="text-xs text-muted-foreground">Pending</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                                            <Users className="w-6 h-6 text-success" />
                                        </div>
                                        <div>
                                            <p className="text-3xl font-bold">{stats?.total_users || 0}</p>
                                            <p className="text-xs text-muted-foreground">Users</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            <Card>
                                <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-lg bg-info/10 flex items-center justify-center">
                                            <Shield className="w-6 h-6 text-info" />
                                        </div>
                                        <div>
                                            <p className="text-3xl font-bold">{stats?.total_officers || 0}</p>
                                            <p className="text-xs text-muted-foreground">Officers</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Charts */}
                        <div className="grid lg:grid-cols-2 gap-6">
                            {/* Status Distribution */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Status Distribution</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={80}
                                                    paddingAngle={5}
                                                    dataKey="value"
                                                    label={({ name, value }) => `${name}: ${value}`}
                                                >
                                                    {pieData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="flex justify-center gap-4 mt-4">
                                        {pieData.map((entry, idx) => (
                                            <div key={idx} className="flex items-center gap-2">
                                                <div 
                                                    className="w-3 h-3 rounded-full" 
                                                    style={{ backgroundColor: entry.fill }}
                                                />
                                                <span className="text-sm text-muted-foreground">
                                                    {entry.name}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Violations by Type */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg">Violations by Type</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={analytics?.by_type?.slice(0, 6) || []}>
                                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                                <XAxis 
                                                    dataKey="type" 
                                                    tick={{ fontSize: 12 }}
                                                    className="text-muted-foreground"
                                                />
                                                <YAxis tick={{ fontSize: 12 }} />
                                                <Tooltip 
                                                    contentStyle={{ 
                                                        backgroundColor: 'hsl(var(--card))',
                                                        borderColor: 'hsl(var(--border))'
                                                    }}
                                                />
                                                <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Daily Reports */}
                            <Card className="lg:col-span-2">
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <TrendingUp className="w-5 h-5" />
                                        Reports (Last 7 Days)
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={analytics?.daily || []}>
                                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                                <XAxis 
                                                    dataKey="date" 
                                                    tick={{ fontSize: 12 }}
                                                    tickFormatter={(date) => format(new Date(date), 'MMM d')}
                                                />
                                                <YAxis tick={{ fontSize: 12 }} />
                                                <Tooltip 
                                                    labelFormatter={(date) => format(new Date(date), 'PPP')}
                                                    contentStyle={{ 
                                                        backgroundColor: 'hsl(var(--card))',
                                                        borderColor: 'hsl(var(--border))'
                                                    }}
                                                />
                                                <Line 
                                                    type="monotone" 
                                                    dataKey="count" 
                                                    stroke="hsl(var(--primary))" 
                                                    strokeWidth={2}
                                                    dot={{ fill: 'hsl(var(--primary))' }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Users Tab */}
                    <TabsContent value="users" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle>User Management</CardTitle>
                                        <CardDescription>
                                            Manage users and their roles
                                        </CardDescription>
                                    </div>
                                    <div className="flex gap-2">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                placeholder="Search users..."
                                                value={userSearch}
                                                onChange={(e) => setUserSearch(e.target.value)}
                                                className="pl-9 w-[200px]"
                                                data-testid="user-search-input"
                                            />
                                        </div>
                                        <Select value={userRoleFilter} onValueChange={(v) => { setUserRoleFilter(v); }}>
                                            <SelectTrigger className="w-[130px]" data-testid="user-role-filter">
                                                <SelectValue placeholder="Role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Roles</SelectItem>
                                                <SelectItem value="user">Users</SelectItem>
                                                <SelectItem value="officer">Officers</SelectItem>
                                                <SelectItem value="admin">Admins</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Role</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Joined</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredUsers.map(u => (
                                            <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                                                <TableCell className="font-medium">{u.name}</TableCell>
                                                <TableCell>{u.email}</TableCell>
                                                <TableCell>
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
                                                        u.role === 'admin' ? 'bg-primary/10 text-primary' :
                                                        u.role === 'officer' ? 'bg-info/10 text-info' :
                                                        'bg-muted text-muted-foreground'
                                                    }`}>
                                                        {u.role}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    {u.is_suspended ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-destructive/10 text-destructive" data-testid={`suspended-badge-${u.user_id}`}>
                                                            <Ban className="w-3 h-3" /> Suspended
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success">
                                                            <CheckCircle2 className="w-3 h-3" /> Active
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {format(new Date(u.created_at), 'MMM d, yyyy')}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Select
                                                            value={u.role}
                                                            onValueChange={(newRole) => handleRoleChange(u.user_id, newRole)}
                                                        >
                                                            <SelectTrigger className="w-[110px]" data-testid={`change-role-${u.user_id}`}>
                                                                <UserCog className="w-4 h-4 mr-2" />
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                <SelectItem value="user">User</SelectItem>
                                                                <SelectItem value="officer">Officer</SelectItem>
                                                                <SelectItem value="admin">Admin</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                        <Button
                                                            variant={u.is_suspended ? 'outline' : 'destructive'}
                                                            size="sm"
                                                            onClick={() => handleToggleSuspend(u.user_id, u.is_suspended)}
                                                            disabled={u.user_id === user?.user_id}
                                                            data-testid={`suspend-toggle-${u.user_id}`}
                                                        >
                                                            {u.is_suspended ? (
                                                                <>
                                                                    <UserCheck className="w-3.5 h-3.5 mr-1" /> Activate
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Ban className="w-3.5 h-3.5 mr-1" /> Suspend
                                                                </>
                                                            )}
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {filteredUsers.length === 0 && (
                                    <div className="text-center py-8 text-muted-foreground">
                                        No users found
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Officer Applications Tab */}
                    <TabsContent value="officer-applications" className="space-y-4">
                        {/* Pending Applications */}
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <Clock className="w-5 h-5 text-warning" />
                                            Pending Applications
                                            <span className="text-sm font-normal text-muted-foreground" data-testid="pending-apps-count">
                                                ({pendingApplications.length})
                                            </span>
                                        </CardTitle>
                                        <CardDescription>
                                            Officer registrations awaiting your approval
                                        </CardDescription>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={fetchOfficerApplications}
                                        data-testid="refresh-applications-btn"
                                    >
                                        <RefreshCw className="w-3.5 h-3.5 mr-1" />
                                        Refresh
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {pendingApplications.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground text-sm">
                                        <BadgeCheck className="w-8 h-8 mx-auto mb-2 text-success" />
                                        No pending officer applications.
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>
                                                    <span className="flex items-center gap-1">
                                                        <Hash className="w-3.5 h-3.5" />
                                                        Employment ID
                                                    </span>
                                                </TableHead>
                                                <TableHead>
                                                    <span className="flex items-center gap-1">
                                                        <MapPin className="w-3.5 h-3.5" />
                                                        Division Area
                                                    </span>
                                                </TableHead>
                                                <TableHead>Applied</TableHead>
                                                <TableHead className="text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {pendingApplications.map(app => (
                                                <TableRow key={app.user_id} data-testid={`app-row-${app.user_id}`}>
                                                    <TableCell className="font-medium">{app.name}</TableCell>
                                                    <TableCell>{app.email}</TableCell>
                                                    <TableCell className="font-mono">{app.employment_id}</TableCell>
                                                    <TableCell>{app.division_area}</TableCell>
                                                    <TableCell className="text-sm">
                                                        {format(new Date(app.created_at), 'MMM d, yyyy')}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleApproveOfficer(app.user_id)}
                                                                disabled={appActionLoading === app.user_id}
                                                                data-testid={`approve-btn-${app.user_id}`}
                                                            >
                                                                {appActionLoading === app.user_id ? (
                                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                ) : (
                                                                    <>
                                                                        <UserCheck className="w-3.5 h-3.5 mr-1" />
                                                                        Approve
                                                                    </>
                                                                )}
                                                            </Button>
                                                            <Button
                                                                variant="destructive"
                                                                size="sm"
                                                                onClick={() => handleRejectOfficer(app.user_id)}
                                                                disabled={appActionLoading === app.user_id}
                                                                data-testid={`reject-btn-${app.user_id}`}
                                                            >
                                                                {appActionLoading === app.user_id ? (
                                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                ) : (
                                                                    <>
                                                                        <XCircle className="w-3.5 h-3.5 mr-1" />
                                                                        Reject
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>

                        {/* Rejected Applications */}
                        {rejectedApplications.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-destructive">
                                        <XCircle className="w-5 h-5" />
                                        Rejected Applications ({rejectedApplications.length})
                                    </CardTitle>
                                    <CardDescription>
                                        Previously rejected officer registrations
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Employment ID</TableHead>
                                                <TableHead>Division Area</TableHead>
                                                <TableHead>Rejected</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {rejectedApplications.map(app => (
                                                <TableRow key={app.user_id} data-testid={`rejected-row-${app.user_id}`} className="opacity-60">
                                                    <TableCell className="font-medium">{app.name}</TableCell>
                                                    <TableCell>{app.email}</TableCell>
                                                    <TableCell className="font-mono">{app.employment_id}</TableCell>
                                                    <TableCell>{app.division_area}</TableCell>
                                                    <TableCell className="text-sm">
                                                        {app.rejected_at ? format(new Date(app.rejected_at), 'MMM d, yyyy') : '—'}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* Violations Tab */}
                    <TabsContent value="violations" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <CardTitle>All Violations</CardTitle>
                                        <CardDescription>
                                            View and manage all violation reports
                                        </CardDescription>
                                    </div>
                                    <Select value={violationStatusFilter} onValueChange={setViolationStatusFilter}>
                                        <SelectTrigger className="w-[150px]" data-testid="violation-status-filter">
                                            <SelectValue placeholder="Filter Status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Status</SelectItem>
                                            <SelectItem value="pending">Pending</SelectItem>
                                            <SelectItem value="approved">Approved</SelectItem>
                                            <SelectItem value="rejected">Rejected</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Vehicle</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Reporter</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead className="text-right">Details</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {violations.map(v => (
                                            <TableRow key={v.id} data-testid={`violation-row-${v.id}`}>
                                                <TableCell className="font-mono font-medium">
                                                    {v.vehicle_number}
                                                </TableCell>
                                                <TableCell>
                                                    <ViolationTypeBadge type={v.violation_type} />
                                                </TableCell>
                                                <TableCell>{v.user_name}</TableCell>
                                                <TableCell>
                                                    <StatusBadge status={v.status} size="sm" />
                                                </TableCell>
                                                <TableCell>
                                                    {format(new Date(v.created_at), 'MMM d, yyyy')}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Dialog>
                                                        <DialogTrigger asChild>
                                                            <Button variant="ghost" size="sm" data-testid={`view-details-${v.id}`}>
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                        </DialogTrigger>
                                                        <DialogContent>
                                                            <DialogHeader>
                                                                <DialogTitle>{v.vehicle_number}</DialogTitle>
                                                                <DialogDescription>
                                                                    Violation Details
                                                                </DialogDescription>
                                                            </DialogHeader>
                                                            <div className="space-y-4">
                                                                <div className="grid grid-cols-2 gap-4 text-sm">
                                                                    <div>
                                                                        <p className="text-muted-foreground">Type</p>
                                                                        <ViolationTypeBadge type={v.violation_type} />
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-muted-foreground">Status</p>
                                                                        <StatusBadge status={v.status} />
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-muted-foreground">Location</p>
                                                                        <p>{v.location || 'Not specified'}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-muted-foreground">Reported</p>
                                                                        <p>{format(new Date(v.created_at), 'PPp')}</p>
                                                                    </div>
                                                                </div>
                                                                {v.description && (
                                                                    <div>
                                                                        <p className="text-muted-foreground text-sm">Description</p>
                                                                        <p className="text-sm">{v.description}</p>
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <p className="text-muted-foreground text-sm">Reporter</p>
                                                                    <p className="text-sm">{v.user_name} ({v.user_email})</p>
                                                                </div>
                                                                {v.reviewer_name && (
                                                                    <div>
                                                                        <p className="text-muted-foreground text-sm">Reviewed by</p>
                                                                        <p className="text-sm">{v.reviewer_name}</p>
                                                                        {v.review_remarks && (
                                                                            <p className="text-sm text-muted-foreground mt-1">
                                                                                "{v.review_remarks}"
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </DialogContent>
                                                    </Dialog>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {violations.length === 0 && (
                                    <div className="text-center py-8 text-muted-foreground">
                                        No violations found
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* API Logs Tab */}
                    <TabsContent value="logs" className="space-y-4">
                        {/* Retry Queue */}
                        <Card className="border-warning/30">
                            <CardHeader>
                                <div className="flex items-center justify-between gap-4">
                                    <div>
                                        <CardTitle className="flex items-center gap-2">
                                            <AlertTriangle className="w-5 h-5 text-warning" />
                                            Government API Retry Queue
                                            <span className="text-xs font-normal text-muted-foreground" data-testid="retry-queue-count">
                                                ({retryQueue.length})
                                            </span>
                                        </CardTitle>
                                        <CardDescription>
                                            Approved violations that have not been successfully accepted by the government system
                                        </CardDescription>
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => Promise.all([fetchRetryQueue(), fetchApiLogs()])}
                                        data-testid="refresh-queue-btn"
                                    >
                                        <RefreshCw className="w-3.5 h-3.5 mr-1" />
                                        Refresh
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {retryQueue.length === 0 ? (
                                    <div className="text-center py-6 text-muted-foreground text-sm">
                                        <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-success" />
                                        All approved violations have been delivered to the government system.
                                    </div>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Violation</TableHead>
                                                <TableHead>Vehicle</TableHead>
                                                <TableHead>Reviewed</TableHead>
                                                <TableHead>Last Error</TableHead>
                                                <TableHead className="text-right">Action</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {retryQueue.map(v => (
                                                <TableRow key={v.id} data-testid={`retry-row-${v.id}`}>
                                                    <TableCell className="font-mono text-xs">{v.id}</TableCell>
                                                    <TableCell className="font-mono font-medium">{v.vehicle_number}</TableCell>
                                                    <TableCell className="text-sm">
                                                        {v.reviewed_at ? format(new Date(v.reviewed_at), 'MMM d, p') : '—'}
                                                    </TableCell>
                                                    <TableCell className="text-xs text-muted-foreground max-w-[260px] truncate">
                                                        {v.api_response?.error || 'Pending delivery'}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <Button
                                                            size="sm"
                                                            onClick={() => handleRetryGovtApi(v.id)}
                                                            disabled={retryingId === v.id}
                                                            data-testid={`retry-btn-${v.id}`}
                                                        >
                                                            {retryingId === v.id ? (
                                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                            ) : (
                                                                <>
                                                                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                                                                    Retry
                                                                </>
                                                            )}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                            </CardContent>
                        </Card>

                        {/* Full API Logs */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Government API Logs</CardTitle>
                                <CardDescription>
                                    Full history of API attempts to the government traffic system
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Violation ID</TableHead>
                                            <TableHead>Attempt</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Response</TableHead>
                                            <TableHead>Timestamp</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {apiLogs.map(log => {
                                            const isSuccess = log.response_status === 200;
                                            return (
                                                <TableRow key={log.id} data-testid={`log-row-${log.id}`}>
                                                    <TableCell className="font-mono text-xs">
                                                        {log.violation_id}
                                                    </TableCell>
                                                    <TableCell className="text-xs">
                                                        #{log.attempt || 1}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                                            isSuccess
                                                                ? 'bg-success/10 text-success'
                                                                : 'bg-destructive/10 text-destructive'
                                                        }`}>
                                                            {isSuccess ? (
                                                                <CheckCircle2 className="w-3 h-3" />
                                                            ) : (
                                                                <XCircle className="w-3 h-3" />
                                                            )}
                                                            {log.response_status}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-sm max-w-[280px] truncate">
                                                        {log.response_body?.reference_id || log.response_body?.error || log.response_body?.message}
                                                    </TableCell>
                                                    <TableCell className="text-xs">
                                                        {format(new Date(log.created_at), 'MMM d, p')}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                                {apiLogs.length === 0 && (
                                    <div className="text-center py-8 text-muted-foreground">
                                        No API logs yet
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Officer Performance Tab */}
                    <TabsContent value="performance" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Officer Performance</CardTitle>
                                <CardDescription>Review volume, approval rate and last activity per officer</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Officer</TableHead>
                                            <TableHead className="text-right">Reviewed</TableHead>
                                            <TableHead className="text-right">Approved</TableHead>
                                            <TableHead className="text-right">Rejected</TableHead>
                                            <TableHead className="text-right">Approval rate</TableHead>
                                            <TableHead>Last review</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {officerPerformance.map(o => (
                                            <TableRow key={o.officer_id} data-testid={`perf-row-${o.officer_id}`}>
                                                <TableCell className="font-medium">{o.name}</TableCell>
                                                <TableCell className="text-right">{o.total}</TableCell>
                                                <TableCell className="text-right text-success">{o.approved}</TableCell>
                                                <TableCell className="text-right text-destructive">{o.rejected}</TableCell>
                                                <TableCell className="text-right">
                                                    {o.approval_rate != null ? `${Math.round(o.approval_rate * 100)}%` : '—'}
                                                </TableCell>
                                                <TableCell className="text-xs text-muted-foreground">
                                                    {o.last_review ? format(new Date(o.last_review), 'MMM d, p') : '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {officerPerformance.length === 0 && (
                                    <div className="text-center py-8 text-muted-foreground">No reviews recorded yet</div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Map Tab */}
                    <TabsContent value="map" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Violations Map</CardTitle>
                                <CardDescription>
                                    Geotagged reports{violationStatusFilter !== 'all' ? ` (${violationStatusFilter})` : ''} — colour indicates status
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <ViolationsMap violations={violations} />
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Audit Tab */}
                    <TabsContent value="audit" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle>Admin Audit Log</CardTitle>
                                <CardDescription>Privileged actions — role changes, suspensions, officer approvals</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>When</TableHead>
                                            <TableHead>Actor</TableHead>
                                            <TableHead>Action</TableHead>
                                            <TableHead>Target</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {auditLogs.map(a => (
                                            <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                                                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                                                    {format(new Date(a.created_at), 'MMM d, p')}
                                                </TableCell>
                                                <TableCell className="text-sm">{a.actor_name || a.actor_id}</TableCell>
                                                <TableCell>
                                                    <span className="font-mono text-xs px-2 py-0.5 rounded bg-muted">{a.action}</span>
                                                </TableCell>
                                                <TableCell className="text-xs font-mono text-muted-foreground">
                                                    {a.target_id || '—'}
                                                    {a.meta?.reason ? ` · ${a.meta.reason}` : ''}
                                                    {a.meta?.role ? ` · ${a.meta.role}` : ''}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {auditLogs.length === 0 && (
                                    <div className="text-center py-8 text-muted-foreground">No admin actions recorded yet</div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
